import React, { useEffect, useRef } from 'react'
import { Box, Typography, MenuItem, Grid, Button, CircularProgress } from '@mui/material'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import { CustomSelect, StandardChip } from '../../../v2/MUI_custom_component'
import ProductCard from './component/ProductCard'
import CartIem from './component/CartIem'
import { getSkuCategoryDetailAction, GetCategoryProductsAction } from './API/Action'
import { DP_ROUTE_SOURCE } from './Enums'
import {
  addProductToCart,
  updateProductQty,
  removeProduct,
  clearCartItems,
  initDP,
  resetDP,
  setDPDetails,
  selectShipToParty,
  setLoading
} from './reducer/DpActions'
import { formType as FORM_TYPE_ENUM } from '../../../utils/settings'
import HumbeeModal from '../../../v2/MUI_custom_component/Modal/HumbeeModal'
import DisclaimerSvg from '../../Illustrration/components/DisclaimerSvg'
import {
  selectCurrentCart,
  selectOrderBookingId,
  selectSelectedShipToPartyId,
  selectShipToPartyCarts,
  selectReferencePricingGeography,
  selectPoOpenQty,
  selectTotalDPQuantity,
  selectMOQ,
  selectMaxDPQuantityAllowed
} from './reducer/DpSelectors'

import IllustrationWrapper from './../../../v3/Illustrration/IllustrationWrapper'
import NoDataFoundSvg from './../../../v3/Illustrration/components/NoDataFoundSvg';

const CreateDp = () => {
  const [productList, setProductList] = React.useState([])
  const [selectedCategory, setSelectedCategory] = React.useState(null)
  const [selectedFormType, setSelectedFormType] = React.useState(null)
  const [pendingFormType, setPendingFormType] = React.useState(null)
  const [skuCategories, setSkuCategories] = React.useState([])
  const [loadingProducts, setLoadingProducts] = React.useState(false)
  
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const previousManufacturerRef = useRef(null)

  const orderBookingId = useSelector(selectOrderBookingId)
  const selectedShipToPartyId = useSelector(selectSelectedShipToPartyId)
  const cartItems = useSelector(selectCurrentCart)
  const shipToPartyCarts = useSelector(selectShipToPartyCarts)
  const referencePricingGeo = useSelector(selectReferencePricingGeography)
  const poRemainingQty = useSelector(selectPoOpenQty) 
  const totalDPQuantity = useSelector(selectTotalDPQuantity)
  const selectMOQData = useSelector(selectMOQ)
  const source = location.state?.from;
  const effectiveShipToPartyId =
  location.state?.shipToPartyId ?? selectedShipToPartyId;
  
  useEffect(() => {
    if (
      !effectiveShipToPartyId &&
      Object.keys(shipToPartyCarts).length > 0
      
    ) {
      console.warn(
        'Blocked general cart usage because Ship-To-Party already exists'
      );
    }
  }, [effectiveShipToPartyId, shipToPartyCarts])

  const { selectedSupplier: selectedManufacturer, selectedEntity: selectedCompanyEntity } = useSelector(
    (state) => state.entityReducer
  )
  const DEFAULT_SKU_PAYLOAD = {
    type_id: 0,
    grade_id: 0,
    nb_id: 0,
    od_id: 0,
    area_id: 0,
    sub_grade_id: 0,
    thickness_id: 0,
    coating_id: 0,
    length_id: 0,
  }
  const getDefaultValueFromCategory = (category) => {
    return category.sku_value_details.find(detail => detail.is_default) || 
      (category.sku_value_details.length > 0 ? category.sku_value_details[0] : null)
  }

  const getSkuProductList = async () => {
    setLoadingProducts(true)
    const { data, error } = await getSkuCategoryDetailAction(selectedManufacturer, DEFAULT_SKU_PAYLOAD)
    
    if (error || !data?.details) {
      setLoadingProducts(false)
      return
    }

    setSkuCategories(data.details)

    const defaultSkuValues = []
    const categories = data.details

    categories.forEach(category => {
      const defaultValue = getDefaultValueFromCategory(category)
      if (defaultValue) {
        defaultSkuValues.push(defaultValue.sku_value_id)
        if (category.sku_key === 'TYPE' || category.sku_key === 'GRADE') {
          setSelectedCategory(defaultValue.sku_value_id.toString())
        } else if (category.sku_key === 'FORM_TYPE') {
          setSelectedFormType(defaultValue.sku_value_id.toString())
        }
      }
    })

    if (defaultSkuValues.length > 0) {
      const payload = { 
        sku_value_ids: defaultSkuValues,
        manufacturer_pricing_geography_id: referencePricingGeo?.pricing_geography_id || null,
        order_booking_id: orderBookingId || null,
        company_esi_id: selectedCompanyEntity,
      }
      await fetchProducts(payload)
    }
    setLoadingProducts(false)
  }

  const fetchProducts = async (payload) => {
    const response = await GetCategoryProductsAction(payload)
    if (!response.error && response.data) {
      setProductList(response.data)
    }
  }

  useEffect(() => {
    if (!selectedCompanyEntity || !selectedManufacturer) return

    // Initialize DP with order_booking_id if provided from navigation state or PO list
    const passedOrderBookingId = location.state?.order_booking_id || location.state?.orderBookingId
    if (passedOrderBookingId && !orderBookingId) {
      dispatch(initDP(passedOrderBookingId))
    }

    if (previousManufacturerRef.current && 
        previousManufacturerRef.current !== selectedManufacturer) {
      dispatch(resetDP())
    }
    
    previousManufacturerRef.current = selectedManufacturer
    getSkuProductList()
  }, [selectedCompanyEntity, selectedManufacturer, location.state])

  const fetchProductsForSelectedCategories = async (gradeValue = null, formTypeValue = null) => {
    const currentGrade = gradeValue || selectedCategory
    const currentFormType = formTypeValue || selectedFormType
    const selectedSkuValues = [currentGrade, currentFormType]
      .filter(Boolean)
      .map(value => parseInt(value))

    if (selectedSkuValues.length === 0) return
    const payload = { 
      sku_value_ids: selectedSkuValues,
      manufacturer_pricing_geography_id: referencePricingGeo?.pricing_geography_id || null,
      order_booking_id: orderBookingId || null,
      company_esi_id: selectedCompanyEntity,
    }
    setLoadingProducts(true)
    const { data, error, message } = await GetCategoryProductsAction(payload)
    setLoadingProducts(false)
    if (!error && data) {
      setProductList(data)
    } else {
      setProductList([])
      if (message) toast.error(message)
    }
  }

  const handleGradeChange = async (event) => {
    const newGradeValue = event.target.value
    setSelectedCategory(newGradeValue)
    await fetchProductsForSelectedCategories(newGradeValue, selectedFormType)
  }

  const getGradeCategory = () => skuCategories?.find(cat => ['GRADE', 'TYPE'].includes(cat.sku_key))
  const getFormTypeCategory = () => skuCategories?.find(cat => cat.sku_key === 'FORM_TYPE')

  // Map a FORM_TYPE sku chip to the payload enum (straight: 1, bend: 2) via its label —
  // sku_value_id is a SKU value id, not the enum value
  const getFormTypeEnumValue = (skuValueId) => {
    const chip = getFormTypeCategory()?.sku_value_details?.find(
      detail => detail.sku_value_id.toString() === skuValueId?.toString()
    )
    return chip?.sku_value?.toLowerCase().includes('bend')
      ? FORM_TYPE_ENUM.BEND
      : FORM_TYPE_ENUM.STRAIGHT
  }

  const getSkuValueIdForFormTypeEnum = (formTypeEnumValue) => {
    const chip = getFormTypeCategory()?.sku_value_details?.find(detail =>
      formTypeEnumValue === FORM_TYPE_ENUM.BEND
        ? detail.sku_value?.toLowerCase().includes('bend')
        : !detail.sku_value?.toLowerCase().includes('bend')
    )
    return chip?.sku_value_id?.toString() ?? null
  }

  // Keep the selected chip in sync with the cart's form type: all products inside
  // one ship-to-party must share a single form type
  const cartFormType = cartItems.find(item => item.form_type)?.form_type ?? null
  useEffect(() => {
    if (!cartFormType || !skuCategories.length) return
    const matchingSkuValueId = getSkuValueIdForFormTypeEnum(cartFormType)
    if (matchingSkuValueId && matchingSkuValueId !== selectedFormType) {
      setSelectedFormType(matchingSkuValueId)
      fetchProductsForSelectedCategories(selectedCategory, matchingSkuValueId)
    }
  }, [cartFormType, skuCategories])

  const getProductQuantity = (productId) => {
    // When coming from ORDER_SUMMARY, only show quantities for newly added products, not existing ones
    if (source === DP_ROUTE_SOURCE.ORDER_SUMMARY || location.state?.from === DP_ROUTE_SOURCE.ORDER_SUMMARY) {
      const product = cartItems.find(item => item.product_id === productId && !item.isExistingItem)
      return product ? (
        typeof product.quantity === 'object' 
          ? product.quantity.quantity_in_metric_ton 
          : Number(product.quantity || 0)
      ) : 0
    }
    
    // For other flows, return 0 to not show earlier added quantities in CreateDP
    return 0;
  }

  const getMaxQuantityForProduct = (productId) => {
    if (!poRemainingQty || poRemainingQty <= 0) {
      return 0
    }
    
    // Get actual cart quantity for limit calculations (not displayed quantity)
    const cartItem = cartItems.find(item => item.product_id === productId)
    const currentQty = cartItem ? (
      typeof cartItem.quantity === 'object' 
        ? cartItem.quantity.quantity_in_metric_ton 
        : Number(cartItem.quantity || 0)
    ) : 0
    
    const remainingPOQty = Math.max(0, poRemainingQty - totalDPQuantity)
    const remainingDPQty = selectMOQData?.max_order_quantity && (selectMOQData.max_order_quantity - totalDPQuantity)
    const maxAllowableQty = Math.min(remainingPOQty, remainingDPQty)  
    const roundToThreeDecimals = (num) => {
      return Math.round((num + Number.EPSILON) * 1000) / 1000
    }  
    return roundToThreeDecimals(currentQty + maxAllowableQty)
  }

  const applyFormType = (newFormTypeValue) => {
    setSelectedFormType(newFormTypeValue)
    fetchProductsForSelectedCategories(selectedCategory, newFormTypeValue)
  }

  const handleFormTypeClick = (formTypeId) => {
    const newFormTypeValue = formTypeId.toString()
    if (newFormTypeValue === selectedFormType) return
    // One form type per ship-to-party: switching with items in the cart clears it,
    // so ask for confirmation first
    if (cartItems.length > 0) {
      setPendingFormType(newFormTypeValue)
      return
    }
    applyFormType(newFormTypeValue)
  }

  const handleConfirmFormTypeChange = () => {
    dispatch(clearCartItems(effectiveShipToPartyId))
    applyFormType(pendingFormType)
    setPendingFormType(null)
  }

  const validatePOLimit = (product, newQty) => {
    if (!poRemainingQty || poRemainingQty <= 0) {
      toast.error('No PO open quantity limit available')
      return false
    }

    // Get actual cart quantity for validation - include all products in ORDER_SUMMARY flow
    const isFromOrderSummary = source === DP_ROUTE_SOURCE.ORDER_SUMMARY || location.state?.from === DP_ROUTE_SOURCE.ORDER_SUMMARY
    const cartItem = cartItems.find(item => item.product_id === product.product_id)
    let currentQty = 0
    
    if (cartItem) {
      currentQty = typeof cartItem.quantity === 'object' 
        ? cartItem.quantity.quantity_in_metric_ton 
        : Number(cartItem.quantity || 0)
    }
    
    const qtyIncrease = newQty - currentQty
    if (qtyIncrease <= 0) {
      return true
    }

    const newTotalQty = totalDPQuantity + qtyIncrease
    const remainingPOQty = Math.max(0, poRemainingQty - totalDPQuantity)
    const remainingDPQty = selectMOQData?.max_order_quantity ? Math.max(0, selectMOQData.max_order_quantity - totalDPQuantity) : Infinity

    const roundToThreeDecimals = (num) => {
      return Math.round((num + Number.EPSILON) * 1000) / 1000
    }
  
    if (newTotalQty > poRemainingQty) {
      const maxAllowedPO = roundToThreeDecimals(currentQty + remainingPOQty)
      if (remainingPOQty <= 0) {
        toast.error(`PO quantity limit of ${roundToThreeDecimals(poRemainingQty)} has been reached. Cannot add more items.`)
        return false
      } else {
        toast.error(`PO Limit reached. Maximum allowed for this item: ${maxAllowedPO} ton`)
        return maxAllowedPO
      }
    }
    if (selectMOQData?.max_order_quantity && (newTotalQty > selectMOQData.max_order_quantity)) {
      const maxAllowedDP = roundToThreeDecimals(currentQty + remainingDPQty)
      
      if (remainingDPQty <= 0) {
        toast.error(`DP quantity limit of ${roundToThreeDecimals(selectMOQData.max_order_quantity)} has been reached. Cannot add more items.`)
        return false
      } else {
        toast.error(`DP Limit reached. Maximum allowed for this item: ${maxAllowedDP} ton`)
        return maxAllowedDP
      }
    }
    
    return true
  }

  const handleQuantityChange = (product, requestedQty) => {
    if (requestedQty <= 0) {
      dispatch(removeProduct(product.product_id, effectiveShipToPartyId))
      return
    }

    const maxQuantity = getMaxQuantityForProduct(product.product_id)
    const validation = validatePOLimit(product, requestedQty)
    
    if (validation === false) {
      return
    }
    
    const finalQty = typeof validation === 'number' ? validation : requestedQty
    if (finalQty === maxQuantity && finalQty > 0) {
      const remainingPOQty = Math.max(0, poRemainingQty - totalDPQuantity)
      const remainingDPQty = selectMOQData?.max_order_quantity ? Math.max(0, selectMOQData.max_order_quantity - totalDPQuantity) : Infinity
      
      if (remainingPOQty <= remainingDPQty) {
        toast.error(`Maximum quantity reached. PO limit: ${finalQty} ton`)
      } else {
        toast.error(`Maximum quantity reached. DP limit: ${finalQty} ton`)
      }
    }
    
    // When adding from ORDER_SUMMARY, check for both new and existing items
    const isFromOrderSummary = source === DP_ROUTE_SOURCE.ORDER_SUMMARY || location.state?.from === DP_ROUTE_SOURCE.ORDER_SUMMARY
    let existingCartItem = cartItems.find(item => item.product_id === product.product_id)
    
    if (isFromOrderSummary) {
      // In ORDER_SUMMARY flow, we might also have existing items - check for all items
      existingCartItem = cartItems.find(item => item.product_id === product.product_id)
    }
    
    if (existingCartItem) {
      dispatch(updateProductQty(product.product_id, finalQty, effectiveShipToPartyId))
    } else {
      const cartProduct = {
        product_id: product.product_id,
        product_name: product.product_name,
        product_image: product.product_image,
        current_price_per_unit: product.current_price_per_unit,
        quantity: {
          quantity_in_bags: finalQty * product.meta_data.multiplier,
          quantity_in_metric_ton: finalQty,
          multiplier: product.meta_data.multiplier,
          unit: product.meta_data.unit,
          base_unit: product.meta_data.base_unit,
        },
        description: product.description,
        form_type: getFormTypeEnumValue(selectedFormType),
        isExistingItem: false, // New products are always marked as non-existing
      }
      
      dispatch(addProductToCart(cartProduct, effectiveShipToPartyId))
    }
  }
  useEffect(() => {
    if (!selectedCategory && !selectedFormType) return;
    dispatch(setDPDetails({
      selectedCategory,
      selectedFormType
    }));
  }, [selectedCategory, selectedFormType]);
  

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h2">
          Select Product
        </Typography>
        <CartIem selectedCompanyData={selectedCompanyEntity} source={source} />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          {getGradeCategory() && (
            <CustomSelect
              label="Sub Category"
              value={selectedCategory}
              onChange={handleGradeChange}
              fullWidth
            >
              {getGradeCategory().sku_value_details.map((grade) => (
                <MenuItem key={grade.sku_value_id} value={grade.sku_value_id.toString()}>
                  <Typography>{grade.sku_value}</Typography>
                </MenuItem>
              ))}
            </CustomSelect>
          )}
        </Grid>
        
        <Grid item xs={6}>
          {getFormTypeCategory() && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="labelD">
                Select Form Type
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {getFormTypeCategory().sku_value_details.map((formType) => {
                  const isSelected = formType.sku_value_id.toString() === selectedFormType
                  return (
                    <StandardChip
                      key={formType.sku_value_id}
                      label={formType.sku_value}
                      selected={isSelected}
                      onClick={() => handleFormTypeClick(formType.sku_value_id)}
                      size="medium"
                      height="unset"
                      sx={{ padding: '4px 8px' }}
                    />
                  )
                })}
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
      
      <Box>
        {loadingProducts ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : productList?.length > 0 ? (
          <Grid container spacing={2}>
            {productList.map((product) => (
              <Grid item xs={12} sm={6} key={product.product_id}>
                <ProductCard
                  imageUrl={product.product_image}
                  name={product.product_name}
                  price={product.current_price_per_unit}
                  quantity={getProductQuantity(product.product_id)}
                  maxQuantity={getMaxQuantityForProduct(product.product_id)}
                  onQuantityChange={(newQty) => handleQuantityChange(product, newQty)}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="#666">
              {productList ?
                <IllustrationWrapper 
                  illustration={<NoDataFoundSvg />}
                  title="No Data"
                />
              : 'Loading products...'}
            </Typography>
          </Box>
        )}
      </Box>
      <HumbeeModal
        open={Boolean(pendingFormType)}
        onClose={() => setPendingFormType(null)}
        onConfirm={handleConfirmFormTypeChange}
        title="Changing form type will clear your cart!"
        subtitle="Products added to the cart will be removed."
        confirmLabel="Yes"
        cancelLabel="No"
        illustration={<DisclaimerSvg />}
        width={{ xs: 400, md: 600 }}
      />
    </Box>
  )
}

export default CreateDp